import React, { useRef, useCallback, KeyboardEvent, ClipboardEvent } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../utils/cn';
import { authEase } from './authMotion';

const OTP_LENGTH = 6;

interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  success?: boolean;
  id?: string;
  'aria-label'?: string;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  value,
  onChange,
  disabled,
  success,
  id = 'verify-otp',
  'aria-label': ariaLabel = '6-digit verification code',
}) => {
  const reduceMotion = useReducedMotion();
  const digits = value.replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
  while (digits.length < OTP_LENGTH) digits.push('');
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);

  const setDigits = useCallback(
    (next: string[]) => {
      onChange(next.join('').replace(/\D/g, '').slice(0, OTP_LENGTH));
    },
    [onChange]
  );

  const focusAt = (index: number) => {
    refs.current[Math.min(Math.max(index, 0), OTP_LENGTH - 1)]?.focus();
  };

  const handleChange = (index: number, char: string) => {
    const d = char.replace(/\D/g, '');
    if (!d) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }
    const next = [...digits];
    let i = index;
    for (const c of d) {
      if (i >= OTP_LENGTH) break;
      next[i] = c;
      i += 1;
    }
    setDigits(next);
    if (i < OTP_LENGTH) focusAt(i);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index]) {
      e.preventDefault();
      focusAt(index - 1);
      const next = [...digits];
      next[Math.max(index - 1, 0)] = '';
      setDigits(next);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusAt(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = pasted.split('');
    while (next.length < OTP_LENGTH) next.push('');
    setDigits(next);
    focusAt(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  const isComplete = digits.every((d) => d.length > 0);

  return (
    <motion.div
      className="flex justify-between gap-2"
      role="group"
      aria-label={ariaLabel}
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: authEase }}
    >
      {digits.map((digit, index) => {
        const isFocused = focusedIndex === index;
        const filled = !!digit;

        return (
          <motion.div key={index} className="relative flex-1 max-w-[3rem]">
            <motion.span
              className="pointer-events-none absolute inset-0 rounded-xl"
              animate={{
                boxShadow:
                  success
                    ? '0 0 0 2px rgb(16 185 129 / 0.35)'
                    : isFocused
                      ? '0 0 0 2px rgb(59 130 246 / 0.35), 0 0 12px rgb(59 130 246 / 0.12)'
                      : '0 0 0 0px transparent',
              }}
              transition={{ duration: 0.2 }}
              aria-hidden
            />
            <input
              ref={(el) => {
                refs.current[index] = el;
              }}
              id={index === 0 ? id : undefined}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              value={digit}
              disabled={disabled}
              aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
              className={cn(
                'relative h-12 w-full rounded-xl border bg-white text-center text-lg font-semibold text-gray-900',
                'transition-[border-color,background-color] duration-200',
                'focus:border-blue-500 focus:outline-none',
                'disabled:cursor-not-allowed disabled:bg-gray-50',
                success && 'border-emerald-400 bg-emerald-50/50',
                filled && !isFocused && !success && 'border-blue-200 bg-blue-50/30',
                !filled && !isFocused && !success && 'border-gray-300',
                isFocused && !success && 'border-blue-500'
              )}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={(e) => {
                setFocusedIndex(index);
                e.target.select();
              }}
              onBlur={() => setFocusedIndex((prev) => (prev === index ? null : prev))}
            />
            {isFocused && !reduceMotion && !success ? (
              <motion.span
                className="absolute bottom-2 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-blue-500"
                layoutId="otp-caret"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                aria-hidden
              />
            ) : null}
          </motion.div>
        );
      })}
      <span className="sr-only" role="status" aria-live="polite">
        {success
          ? 'Verification code accepted'
          : isComplete
            ? 'Code complete'
            : `${digits.filter(Boolean).length} of ${OTP_LENGTH} digits entered`}
      </span>
    </motion.div>
  );
};

export const AuthOTPInput = OTPInput;
